import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  CRM_SEARCH_MIN_LENGTH,
  ListAccountsResponse,
  SearchHubspotCompaniesResponse,
  listAccountsContract,
  searchHubspotCompaniesContract
} from '@bliks-io/contracts';
import { apiClient } from '@/api/client';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

/**
 * Jeder Tastendruck wäre sonst eine Anfrage gegen ein Such-Limit, das sich
 * Partnerbereich und n8n teilen: der Begriff wird erst gelesen, wenn er kurz steht.
 */
const SEARCH_SETTLE_MS = 300;

export const accountKeys = {
  all: ['accounts'] as const,
  list: () => [...accountKeys.all, 'list'] as const,
  companySearch: (term: string) => [...accountKeys.all, 'company-search', term] as const
};

/**
 * The super-admin's customer list — the level above tenants (LIC-06).
 *
 * Company names come from HubSpot per request, so the response carries
 * `companiesResolved`: a false there means the names are missing because the CRM
 * could not be reached, not because the customers have none.
 */
export function useListAccounts() {
  return useQuery({
    queryKey: accountKeys.list(),
    queryFn: async ({ signal }): Promise<ListAccountsResponse> => {
      const { data } = await apiClient.get<ListAccountsResponse>(listAccountsContract.path, {
        signal
      });
      return data;
    }
  });
}

/**
 * Company picker for assigning an account its commercial identity.
 *
 * Search by what a person knows, assign by id — the split exists because a
 * name-based lookup once sent an invoice to an unrelated company. Disabled
 * below three characters: a one-letter query returns the CRM, not an answer.
 */
export function useSearchHubspotCompanies(query: string) {
  const term = useDebouncedValue(query.trim(), SEARCH_SETTLE_MS);
  return useQuery({
    queryKey: accountKeys.companySearch(term),
    enabled: term.length >= CRM_SEARCH_MIN_LENGTH,
    // Die alte Liste bleibt stehen, bis die neue da ist — kein Flackern je Zeichen.
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }): Promise<SearchHubspotCompaniesResponse> => {
      const { data } = await apiClient.get<SearchHubspotCompaniesResponse>(
        searchHubspotCompaniesContract.path,
        { params: { q: term }, signal }
      );
      return data;
    }
  });
}
